import React, { useState, useEffect, useRef } from 'react';
import { Form, InputGroup, ListGroup } from 'react-bootstrap';
import { useMap } from 'react-leaflet';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

const SearchControl = ({ onLocationSelect }) => {
  const map = useMap();
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);

  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google?.maps) {
        autocompleteServiceRef.current =
          new window.google.maps.places.AutocompleteService();
        placesServiceRef.current = new window.google.maps.places.PlacesService(
          document.createElement('div')
        );
      }
    };

    if (window.google?.maps) {
      loadGoogleMaps();
    } else {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.onload = loadGoogleMaps;
      document.head.appendChild(script);
    }
  }, []);

  const calculateAccuracyFromViewport = viewport => {
    const sw = viewport.getSouthWest();
    const ne = viewport.getNorthEast();
    const latDiff = Math.abs(ne.lat() - sw.lat());
    const lngDiff = Math.abs(ne.lng() - sw.lng());
    return Math.max(latDiff, lngDiff) * 100000;
  };

  const fetchSuggestions = debounce(input => {
    const service = autocompleteServiceRef.current;
    if (!input || !service) {
      setSuggestions([]);
      return;
    }

    service.getPlacePredictions(
      { input, types: ['geocode'] },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          setSuggestions(predictions || []);
        } else {
          console.warn('Failed to fetch suggestions', status);
          setSuggestions([]);
        }
      }
    );
  }, 300);

  const handleInputChange = e => {
    const input = e.target.value;
    setSearchInput(input);
    fetchSuggestions(input);
  };

  const handleSuggestionSelect = async placeId => {
    setIsLoading(true);
    try {
      const service = placesServiceRef.current;
      if (!service) {
        alert('Google Places service not initialized.');
        return;
      }

      service.getDetails({ placeId }, (place, status) => {
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !place.geometry
        ) {
          console.error('Failed to fetch place details', status);
          alert('Could not fetch location details.');
          return;
        }

        const lat = place.geometry?.location?.lat?.();
        const lng = place.geometry?.location?.lng?.();

        if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) {
          console.error('Invalid lat/lng:', { lat, lng, place });
          alert('Invalid location selected. Please try another.');
          return;
        }

        const address = place.formatted_address || '';
        const accuracy = place.geometry.viewport
          ? Math.round(calculateAccuracyFromViewport(place.geometry.viewport))
          : 20;

        map.flyTo([lat, lng], 13);
        onLocationSelect({ lat: lat, lng: lng, address, accuracy });
      });
    } catch (error) {
      console.error('Unexpected error fetching place details:', error);
      alert('An error occurred while selecting the location.');
    } finally {
      setIsLoading(false);
      setSuggestions([]);
      setSearchInput('');
    }
  };

  return (
    <div
      className="leaflet-top leaflet-left"
      style={{
        position: 'absolute',
        top: '1px',
        zIndex: 1000,
        width: '75%',
        maxWidth: '300px'
      }}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      <div className="leaflet-control leaflet-bar">
        <div style={{ borderRadius: '4px', width: '100%', minWidth: '100px' }}>
          <Form>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search location..."
                value={searchInput}
                onChange={handleInputChange}
                disabled={isLoading}
                onFocus={e => e.stopPropagation()}
              />
            </InputGroup>
          </Form>
          <ListGroup
            style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '5px' }}
          >
            {suggestions.map(suggestion => (
              <ListGroup.Item
                key={suggestion.place_id}
                action
                onClick={e => {
                  e.stopPropagation();
                  handleSuggestionSelect(suggestion.place_id);
                }}
              >
                {suggestion.description}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      </div>
    </div>
  );
};

SearchControl.propTypes = {
  onLocationSelect: PropTypes.func.isRequired
};

export default SearchControl;
